import { useEffect, useState, type FormEvent } from 'react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Select'
import { ORGANIZATION_FORM_CURRENCIES } from '../../utils/currencies'
import { slugify } from '../../utils/slugify'
import { storesApi } from '../../services/storesApi'

const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

type Props = {
  open: boolean
  onClose: () => void
  organisationId: string | number
  defaultCurrency: string
  onCreated: (store: unknown) => void
}

export function AddStoreModal({ open, onClose, organisationId, defaultCurrency, onCreated }: Props) {
  const [name, setName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  /** Empty = use company usual currency (omit from create payload). */
  const [currency, setCurrency] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [sellingSummary, setSellingSummary] = useState('')
  const [autoSlug, setAutoSlug] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const companyCurrency = String(defaultCurrency || 'USD').trim().toUpperCase() || 'USD'

  useEffect(() => {
    if (!open) {
      setName('')
      setSubdomain('')
      setCurrency('')
      setContactEmail('')
      setContactPhone('')
      setSellingSummary('')
      setAutoSlug(true)
      setErrors({})
      setFormError(null)
      setSubmitting(false)
    }
  }, [open, defaultCurrency])

  function validate() {
    const next: Record<string, string> = {}
    const trimmedName = name.trim()
    if (trimmedName.length < 2) next.name = 'Shop name must be at least 2 characters.'
    const sub = subdomain.trim().toLowerCase()
    if (!sub) next.subdomain = 'Shop web address is required.'
    else if (!SUBDOMAIN_RE.test(sub)) next.subdomain = 'Use lowercase letters, numbers, and hyphens only.'
    const email = contactEmail.trim()
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.contactEmail = 'Enter a valid shop email, or leave blank.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const chosenCurrency = currency.trim().toUpperCase()
      const created = await storesApi.create(organisationId, {
        name: name.trim(),
        subdomain: subdomain.trim().toLowerCase(),
        ...(chosenCurrency ? { currency: chosenCurrency } : {}),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        sellingSummary: sellingSummary.trim() || undefined,
      })
      onCreated(created)
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create store'
      setFormError(message)
      const code = (err as { code?: string }).code
      if (code === 'SUBDOMAIN_IN_USE' || /subdomain/i.test(message)) {
        setErrors((prev) => ({ ...prev, subdomain: message }))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      title="Add store"
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="add-store-form" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create store'}
          </Button>
        </>
      }
    >
      <form
        id="add-store-form"
        className="space-y-4"
        noValidate
        onSubmit={(ev) => void handleSubmit(ev)}
      >
        <p className="text-sm text-muted-foreground">
          Add another online shop for this company. Only the shop name and web address are needed
          now — you can fill in the rest later.
        </p>
        {formError ? (
          <p
            className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            {formError}
          </p>
        ) : null}

        <Input
          id="store-name"
          label="Shop name"
          value={name}
          onChange={(e) => {
            const v = e.target.value
            setName(v)
            if (autoSlug) setSubdomain(slugify(v))
          }}
          error={errors.name}
          disabled={submitting}
        />

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <label htmlFor="store-subdomain" className="text-sm font-medium text-foreground">
              Shop web address
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={autoSlug}
                onChange={(e) => {
                  const on = e.target.checked
                  setAutoSlug(on)
                  if (on) setSubdomain(slugify(name))
                }}
                className="rounded border-border"
                disabled={submitting}
              />
              Fill from shop name
            </label>
          </div>
          <input
            id="store-subdomain"
            disabled={autoSlug || submitting}
            className="w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60"
            value={subdomain}
            onChange={(e) => {
              setAutoSlug(false)
              setSubdomain(e.target.value.toLowerCase())
            }}
            placeholder="acme-store"
            autoComplete="off"
          />
          {errors.subdomain ? (
            <p className="mt-1 text-xs text-destructive">{errors.subdomain}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Short name used in the shop link (lowercase letters, numbers, and hyphens).
            </p>
          )}
        </div>

        <div className="space-y-4 border-t border-border pt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Optional — can fill in later
          </p>

          <Select
            id="store-currency"
            label="Currency customers pay in"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={submitting}
          >
            <option value="">Company usual ({companyCurrency})</option>
            {ORGANIZATION_FORM_CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </Select>

          <Input
            id="store-contact-email"
            type="email"
            label="Shop email (shown to customers)"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            error={errors.contactEmail}
            disabled={submitting}
            autoComplete="off"
          />
          <Input
            id="store-contact-phone"
            type="tel"
            label="Shop phone (shown to customers)"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            disabled={submitting}
            autoComplete="off"
          />
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium text-foreground">What this shop sells</span>
            <textarea
              id="store-selling-summary"
              rows={3}
              value={sellingSummary}
              onChange={(e) => setSellingSummary(e.target.value)}
              disabled={submitting}
              className="min-h-[4.5rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm"
              placeholder="e.g. Ready-to-wear dresses and custom sewing for women"
            />
          </label>
        </div>
      </form>
    </Modal>
  )
}
